import * as PIXI from "pixi.js";
import {TextureButton} from "../core/Button";
import {Container} from "../core/Container";
import {Dict, Mix} from "../core/Reflection";
import {Signal} from "../core/Signal";
import {Sprite} from "../core/Sprite";
import {LevelNumber} from "../data/LevelModel";
import {Location} from "../data/Location";
import {CardNumber, isCardExcluded} from "../data/LocationCardType";
import {ColorOverlayFilter} from "../filters/ColorOverlayFilter";
import {GlowFilter} from "../filters/GlowFilter";
import {OpaqueFilter} from "../filters/OpaqueFilter";
import {Focusable} from "./Focusable";
import {Keyboard} from "./Keyboard";

export class PostcardItem extends Container {

    public postcardItemType: PostcardItemType = PostcardItemType.Decoration;
    public postcardItemIndex: number = 0;
    public dependsOn: number = 0;
    public outlineGlow: boolean = false;
    public outlineGlowUnder: boolean = false;

    public readonly location: Location;
    public readonly level: LevelNumber;
    public readonly onOver = Signal<(item: PostcardItem) => void>();
    public readonly onOut = Signal<(item: PostcardItem) => void>();
    public readonly onClick = Signal<(item: PostcardItem, event?: PIXI.interaction.InteractionEvent) => void>();

    private button?: PostcardItemButton;
    private sprite?: Sprite;
    private glowSprite?: Sprite;

    constructor(location: Location, level: LevelNumber) {
        super();
        this.location = location;
        this.level = level;
    }

    public setLayoutTexture(texture: string | PIXI.Texture, anchorX: number, anchorY: number) {
        if (typeof texture === "string") {
            const e = /^(piece|outline|decoration)s?-\w+-\d-(\d)/.exec(texture);
            if (e && e[1] && e[2]) {
                this.postcardItemType = e[1] as PostcardItemType;
                if (this.postcardItemType !== PostcardItemType.Decoration) {
                    this.postcardItemIndex = parseInt(e[2], 10);
                }
                // Postcard piece or outline without matching cards should be a decoration
                if (isCardExcluded(this.location, this.level, this.postcardItemIndex as CardNumber)) {
                    if (this.postcardItemType === PostcardItemType.Piece) {
                        this.postcardItemType = PostcardItemType.Decoration;
                        this.postcardItemIndex = 0;
                    } else if (this.postcardItemType === PostcardItemType.Outline) {
                        this.postcardItemType = PostcardItemType.Decoration;
                        this.postcardItemIndex = 0;
                        texture = PIXI.Texture.EMPTY;
                    }
                }
            }
        }
        if (this.postcardItemType === PostcardItemType.Piece ||
            this.postcardItemType === PostcardItemType.Decoration) {
            this.button = this.addChild(new PostcardItemButton(this.location));
            this.button.setLayoutTexture(texture, anchorX, anchorY);
            this.button.enabled = false;
            this.button.onOver.add(() => this.onOver(this));
            this.button.onOut.add(() => this.onOut(this));
            this.button.onClick.add((b, e) => this.onClick(this, e));
        } else {
            this.sprite = this.addChild(new Sprite());
            this.sprite.setLayoutTexture(texture, anchorX, anchorY);
        }
    }

    public get isInteractive(): boolean {
        return this.button !== undefined &&
            (this.postcardItemType === PostcardItemType.Piece ||
                (this.postcardItemType === PostcardItemType.Decoration && this.dependsOn > 0));
    }

    public get postcardItemNumber() {
        return this.postcardItemType === PostcardItemType.Decoration && this.dependsOn > 0 ? this.dependsOn :
            this.postcardItemIndex;
    }

    public get enabled(): boolean {
        return this.button?.enabled || false;
    }

    public set enabled(value: boolean) {
        if (this.button) {
            this.button.enabled = value;
            if (value) {
                Keyboard.add(this.button);
                if (this.glowSprite) {
                    this.button.setGlowSprite(this.glowSprite, this.outlineGlowUnder);
                }
            }
        }
    }

    public setShape(value: number[]) {
        if (this.button) {
            const points = [];
            const p = new PIXI.Point();
            let p1 = new PIXI.Point();
            for (let i = 0; i < value.length; i += 2) {
                p.x = value[i];
                p.y = value[i + 1];
                p1 = this.button.toLocal(p, this.parent, p1);
                points.push(p1.x, p1.y);
            }
            this.button.hitArea = new PIXI.Polygon(points);
        }
    }

    public setOutline(outline: PostcardItem) {
        this.glowSprite = outline.sprite || outline.button?.sprite;
    }

    public startGlow(keep?: boolean) {
        if (this.button) {
            this.button.startGlow(keep);
        }
    }

    public endGlow(force?: boolean) {
        if (this.button) {
            this.button.endGlow(force);
        }
    }

    public destroy() {
        this.onOver.remove();
        this.onOut.remove();
        super.destroy();
    }
}

export enum PostcardItemType {
    Piece = "piece",
    Outline = "outline",
    Decoration = "decoration",
}

const LocationGlowColor: Dict<Location, number> = {
    [Location.Beijing]: 0xff783c,
    [Location.Cusco]: 0xac69eb,
    [Location.Munich]: 0x40ff86,
    [Location.Nairobi]: 0xffee63,
    [Location.Rio]: 0xa5f59b,
    [Location.Tokyo]: 0xfb9ddd,
};

class PostcardItemButton extends Mix(TextureButton, Focusable) {

    private glowSprite?: Sprite;
    private keepGlow?: boolean;
    private readonly glowFilter: GlowFilter;
    private readonly opaqueFilter: OpaqueFilter;
    private readonly colorOverlayFilter: ColorOverlayFilter;

    constructor(location: Location) {
        super();

        this.glowFilter = new GlowFilter(18, 6, 1, LocationGlowColor[location]);
        this.opaqueFilter = new OpaqueFilter();
        this.colorOverlayFilter = new ColorOverlayFilter();
        this.opaqueFilter.padding = this.colorOverlayFilter.padding = this.glowFilter.padding;

        this.onOver.add(() => this.startGlow());
        this.onOut.add(() => this.endGlow());
    }

    public setGlowSprite(sprite: Sprite, under: boolean) {
        this.glowSprite = sprite;
        this.glowSprite.visible = false;
        sprite.changeParent(this.visuals);
        if (under) {
            sprite.zIndex = 0;
        }
        this.glowFilter.knockout = true;
    }

    public startGlow(keep?: boolean) {
        if (keep) {
            this.keepGlow = true;
        }
        if (this.glowSprite) {
            this.glowSprite.visible = true;
            this.glowSprite.filters = [
                this.opaqueFilter,
                this.colorOverlayFilter,
                this.glowFilter];
        } else {
            this.filters = [this.glowFilter];
        }
    }

    public endGlow(force?: boolean) {
        if (force) {
            this.keepGlow = false;
            if (this.isOver) {
                return;
            }
        }
        if (this.keepGlow) {
            return;
        }
        if (this.glowSprite) {
            this.glowSprite.visible = false;
            this.glowSprite.filters = null;
        } else {
            this.filters = null;
        }
    }
}
